import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ButtonComponent } from 'ui-library';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';

@Component({
    selector: 'app-report-detail-dialog',
    imports: [
        TranslateModule,
        MatDialogModule,
        CommonModule,
        ButtonComponent,
        MatIconModule,
        MatListModule
    ],
    standalone: true,
    templateUrl: './report-detail-dialog.component.html',
    styleUrls: ['./report-detail-dialog.component.scss']
})
export class ReportDetailDialogComponent implements OnDestroy {
    report: any;

    private readonly destroy$ = new Subject<void>();

    // Resolves each log's appliedConfigId (see unifield-mobile-2's list-update-kyds.page.ts and
    // model/other/report.js) into a displayable template name - same map pattern as
    // kyd.component.ts's own configNameById/appliedConfigName().
    private configNameById = new Map<string, string>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<ReportDetailDialogComponent>,
        private trackerService: TrackerService,
    ) {
        this.report = data.report;

        this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
            this.configNameById = new Map((configs || []).map(c => [c.id, c.name]));
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    appliedConfigName(log: any): string | null {
        return log.appliedConfigId ? (this.configNameById.get(log.appliedConfigId) ?? null) : null;
    }

    // Older reports (sent before the mobile app started tagging each log entry with an
    // `outcome`) don't have that field - fall back to a locale-agnostic substring match on
    // the persisted status text ("success"/"succès" both contain "succ") rather than
    // defaulting to "failed".
    logOutcome(log: any): 'ok' | 'error' | 'skipped' {
        if (log.outcome) return log.outcome;
        if (typeof log.status === 'string' && log.status.toLowerCase().includes('succ')) return 'ok';
        return 'error';
    }

    // unifield-mobile-2's list-update-kyds.page.ts persists distances in centimeters (see
    // buildTrackerSettingsSnapshot()/updatedSettings there) - shown here in meters like
    // everywhere else in the KYD settings UI.
    formatCm(cm: number): string {
        return cm != null ? (cm / 100).toFixed(2) : '-';
    }

    close() {
        this.dialogRef.close();
    }
}
